import { useEffect, useMemo, useState } from "react";
import api from "../utils/api/axiosInstance";
import { buildIdentityCoachFallback } from "../utils/identityCoachUtils";

export default function useIdentityCoach({
  enabled = false,
  identityCompleteness = null,
  productName = null,
  categoryName = null,
  lowConfidence = false,
  reason = "identity_required",
}) {
  const [coaching, setCoaching] = useState(null);
  const [loading, setLoading] = useState(false);

  const localFallback = useMemo(
    () =>
      buildIdentityCoachFallback({
        identityCompleteness,
        productName,
        categoryName,
        lowConfidence,
        reason,
      }),
    [
      identityCompleteness,
      productName,
      categoryName,
      lowConfidence,
      reason,
    ],
  );

  const cacheKey = useMemo(
    () =>
      JSON.stringify({
        enabled,
        missing: identityCompleteness?.missing,
        percent: identityCompleteness?.percent,
        reason,
        lowConfidence,
        productName,
        categoryName,
      }),
    [
      enabled,
      identityCompleteness,
      reason,
      lowConfidence,
      productName,
      categoryName,
    ],
  );

  useEffect(() => {
    if (!enabled) {
      setCoaching(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    api
      .post("/ai/grok/identity-coach", {
        identityCompleteness,
        productName,
        categoryName,
        lowConfidence,
        reason,
      })
      .then((res) => {
        if (!cancelled) setCoaching(res.data?.coaching || localFallback);
      })
      .catch(() => {
        if (!cancelled) setCoaching(localFallback);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, enabled, localFallback]);

  return {
    coaching: loading && !coaching ? null : coaching || localFallback,
    loading,
  };
}
